import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { SpecialtyType } from "@/modules/patients/detail/progress-note/components/specialty-select"
import { useProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { useStoreFormState } from "@/modules/patients/detail/progress-note/hooks/useStoreFormState"
import { mapPointOfCareSpecialty } from "@/modules/patients/detail/progress-note/lib/map-point-of-care-specialty"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { PURPOSE_VALUES } from "@/modules/patients/detail/progress-note/point-of-care/schema/base-schema"
import {
  PointOfCare,
  ProgressNoteBetterDoc,
} from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"

export const useProgressNotePointOfCareForm = (
  id: string,
  pointOfCare: PointOfCare,
  betterDoc: ProgressNoteBetterDoc,
) => {
  const language = useCurrentLanguage()
  const progressNoteForms = useProgressNoteFormContext((state) => state.progressNoteForms)

  const storeForm = progressNoteForms.get(id)?.pointOfCareForm

  const storeFormValue = storeForm?.getValues()

  let defaultValues: Partial<PointOfCareFormType>

  if (storeFormValue) {
    defaultValues = { ...storeFormValue, formIsDirty: false }
  } else {
    const { actual, medical, reasonForChoosingActual, isDoctorVisitAvoided, timeToTreatType, purpose } =
      pointOfCare || {}
    const isBetterDocUsed = betterDoc?.isUsed
    const careProviderId = pointOfCare?.__typename === "ch_PointOfCare" ? pointOfCare.careProvider?.id : null
    const purposeValue = purpose ? PURPOSE_VALUES.filter((value) => purpose[value]) : []
    let selectedSpecialty: SpecialtyType | null | undefined
    if (pointOfCare?.__typename === "ch_PointOfCare") {
      const chSpecialty = pointOfCare.careProviderSpecialty
      const cleanedChSpecialty: SpecialtyType[] =
        chSpecialty?.specialtyTranslations
          .filter((translation) => translation.language === language.toUpperCase())
          .map((translation) => ({
            id: chSpecialty.id,
            language: translation.language,
            word: translation.name,
            synonym: "",
            specialty: {
              id: chSpecialty.id,
              name: chSpecialty.name,
            },
          })) || []
      selectedSpecialty = pointOfCare.pointOfCareSpecialty
        ? mapPointOfCareSpecialty(pointOfCare.pointOfCareSpecialty, language)
        : cleanedChSpecialty[0]
    } else if (pointOfCare?.__typename === "de_PointOfCare") {
      selectedSpecialty = pointOfCare.pointOfCareSpecialty
        ? mapPointOfCareSpecialty(pointOfCare.pointOfCareSpecialty, language)
        : null
    }

    defaultValues = {
      medicalPointOfCare: medical ?? undefined,
      actualPointOfCare: actual ?? undefined,
      reasonForChoosingType: reasonForChoosingActual ?? null,
      isDoctorVisitAvoided,
      timeToTreatType: timeToTreatType ?? null,
      careProviderId,
      purpose: purposeValue,
      specialtyId: selectedSpecialty,
      betterDocSuggestionStatus: isBetterDocUsed && !careProviderId ? "MANUAL_SEARCH_NEEDED" : null,
      formIsDirty: false,
    }
  }

  const storeFormState = useStoreFormState(storeForm)

  return {
    defaultValues,
    storeFormState,
  }
}
